import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { Sections } from '../../shared/sections'
import { api } from './api'
import { dayLabel, listed, localDay, type Who } from './home'
import { firstName } from './tree'

// Home in the Modern style (worker/homeBlend.ts): what's new in a circle since you last looked, as one blend of
// photos, recipes and moments, then "Wander", shelves that follow one person, album or book through the circle.

export type PhotoRef = { id: string; ref: string; width: number; height: number }
// One person adding photos to one album on one day: up to 20 of them, and how many there are in all.
export type PhotosItem = {
  kind: 'photos'
  at: number
  by: Who
  album: { id: string; title: string } | null
  photos: PhotoRef[]
  count: number
}
export type RecipeItem = {
  kind: 'recipe'
  id: string
  title: string
  cover: string | null
  at: number
  by: Who
  book: { id: string; title: string } | null
}
export type MomentItem = {
  kind: 'moment'
  id: string
  title: string
  // What others read (polished or retold if its author chose that), cut for a tile.
  words: string
  happenedOn: string | null
  at: number
  by: Who
  photo: { id: string; width: number; height: number } | null
}
export type NewItem = PhotosItem | RecipeItem | MomentItem
export type HomeNew = {
  since: number
  // No visit before this one to count from, so "new" is Home's usual weeks.
  firstVisit: boolean
  items: NewItem[]
  // A section with more new than Home shows.
  more: { photos: boolean; recipes: boolean; moments: boolean }
}

// One photo on a shelf, with its own caption and date.
export type ShelfPhoto = PhotoRef & { kind: 'photo'; caption: string; takenOn: string | null }
export type ShelfItem = ShelfPhoto | RecipeItem | MomentItem
export type Shelf =
  | {
      kind: 'person'
      person: { id: string; name: string; living: boolean; portrait: string | null }
      items: ShelfItem[]
    }
  | { kind: 'album'; album: { id: string; title: string }; items: ShelfItem[] }
  | { kind: 'book'; book: { id: string; title: string }; items: ShelfItem[] }
export type Wander = { day: string; shelves: Shelf[] }

export type PlaceNews = { id: string; who: Who[] }

const query = (params: Record<string, string | undefined>) => {
  const q = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) if (value) q.set(key, value)
  return q.toString()
}

// With Caught up off (`all`), the feed reaches back past what's new, newest first; `since` still says where new starts.
export const useHomeNew = (familyId: string, spaceId?: string, all = false) =>
  useQuery({
    queryKey: ['home', familyId, spaceId ?? 'all', 'new', all ? 'everything' : 'caught-up'],
    // Flipping Caught up keeps the feed on screen until the other one arrives.
    placeholderData: keepPreviousData,
    queryFn: () =>
      api<HomeNew>(
        `/families/${familyId}/home/new?${query({
          space: spaceId,
          tz: String(-new Date().getTimezoneOffset()),
          all: all ? '1' : undefined,
        })}`,
      ),
  })

export const useWander = (familyId: string, spaceId?: string, day = localDay()) =>
  useQuery({
    queryKey: ['home', familyId, spaceId ?? 'all', 'wander', day],
    queryFn: () => api<Wander>(`/families/${familyId}/home/wander?${query({ space: spaceId, day })}`),
  })

export const usePlaceNews = (enabled = true) =>
  useQuery({
    queryKey: ['places'],
    queryFn: async () => (await api<{ places: PlaceNews[] }>('/places')).places,
    enabled,
  })

// Says you've looked at a circle's Home, once what's new there has loaded, so your next visit counts from now. The
// circle stops showing as new in the place picker at once.
export function useSeeHome(familyId: string, loaded: boolean) {
  const queryClient = useQueryClient()
  const seen = useMutation({
    mutationFn: () => api(`/families/${familyId}/home/seen`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['places'] }),
  })
  const { mutate } = seen
  useEffect(() => {
    if (loaded) mutate()
  }, [familyId, loaded, mutate])
}

// The kinds whose sections are on, in the order the tabs list them.
export const KINDS = ['photos', 'moments', 'recipes'] as const
export type Kind = (typeof KINDS)[number]
export const kindOf = (item: NewItem | ShelfItem): Kind =>
  item.kind === 'recipe' ? 'recipes' : item.kind === 'moment' ? 'moments' : 'photos'
export const KIND_LABELS: Record<Kind, string> = { photos: 'Photos', moments: 'Moments', recipes: 'Recipes' }

// What a chip narrows the new things to: everything, one kind, or one person.
export type Narrow = { kind: Kind } | { by: string } | null

export function narrowed(items: NewItem[], narrow: Narrow, on: Sections) {
  return items.filter(
    (item) =>
      on[kindOf(item)] && (!narrow || ('kind' in narrow ? kindOf(item) === narrow.kind : item.by.id === narrow.by)),
  )
}

// The people with something new, most recent first, each once, for the rings along the top. You aren't news to you.
export function newPeople(items: NewItem[]): Who[] {
  const seen = new Map<string, Who>()
  for (const item of [...items].sort((x, y) => y.at - x.at))
    if (!item.by.you && !seen.has(item.by.id)) seen.set(item.by.id, item.by)
  return [...seen.values()]
}

// The kinds that have anything new, for the chips and the By section shelves.
export const kindsIn = (items: NewItem[]) => KINDS.filter((kind) => items.some((item) => kindOf(item) === kind))

// "since Tuesday", "since yesterday", "since Sep 28", "today": the start of what's new, in the viewer's days.
export function sinceLabel(since: number, now = new Date()) {
  const day = dayLabel(localDay(new Date(since)), now)
  return day === 'today' ? 'today' : `since ${day}`
}

// The place picker's line under a circle: "New from Kit and Rose", or nothing new.
export function placeLine(who: Who[]) {
  const names = who.filter((x) => !x.you).map((x) => firstName(x.name))
  return names.length ? `New from ${listed(names)}` : 'Nothing new since you looked'
}

// What a photos tile says: "Kit added to Summers at the lake", "You added photos".
export function photosLine(item: Pick<PhotosItem, 'by' | 'album'>) {
  const who = item.by.you ? 'You' : firstName(item.by.name)
  return item.album ? `${who} added to ${item.album.title}` : `${who} added photos`
}

// Feed (one thing after another, photos as wide as the screen), Grid (everything on one wall, two wide on a phone) or
// By section (a wall for each), remembered on this device. Grid was called Mixed before Feed came, so its stored name
// stays "mixed"; anyone who never picked gets Feed.
export type Arrange = 'feed' | 'mixed' | 'sections'
const ARRANGE = 'gatheredfrom:home-arrange'
export function arrangeFrom(stored: string | null): Arrange {
  return stored === 'mixed' || stored === 'sections' ? stored : 'feed'
}
export function readArrange(): Arrange {
  try {
    return arrangeFrom(localStorage.getItem(ARRANGE))
  } catch {
    return 'feed'
  }
}
// Caught up: Home's feed shows only what's new since your last visit, ending "You're caught up". Off unless picked,
// so the feed shows everything; remembered on this device like the layout.
const CAUGHT_UP = 'gatheredfrom:home-caught-up'
export const caughtUpFrom = (stored: string | null) => stored === 'on'
export function readCaughtUp(): boolean {
  try {
    return caughtUpFrom(localStorage.getItem(CAUGHT_UP))
  } catch {
    return false
  }
}
export function rememberCaughtUp(on: boolean) {
  try {
    localStorage.setItem(CAUGHT_UP, on ? 'on' : 'off')
  } catch {
    // Not remembered; the choice still holds while the page is open.
  }
}

export function rememberArrange(arrange: Arrange) {
  try {
    localStorage.setItem(ARRANGE, arrange)
  } catch {
    // Not remembered; the choice still holds while the page is open.
  }
}

// A photo in the feed fills the width and keeps its shape, within reason: no taller than 4:5 and no wider than
// 1.91:1, so one tall photo doesn't fill the whole screen and a panorama doesn't become a sliver.
export function feedRatio(width: number, height: number) {
  if (!(width > 0 && height > 0)) return 1
  return Math.min(1.91, Math.max(0.8, width / height))
}

// The dots under a post's photos, as Instagram draws them: all of them for a short set, else a window of `most` that
// follows the photo you're on, with the dots at a window edge drawn small when there are more photos past it.
export function photoDots(count: number, at: number, most = 5) {
  if (count <= most) return Array.from({ length: count }, (_, i) => ({ i, small: false }))
  const start = Math.min(Math.max(at - Math.floor(most / 2), 0), count - most)
  return Array.from({ length: most }, (_, k) => {
    const i = start + k
    return { i, small: (k === 0 && i > 0) || (k === most - 1 && i < count - 1) }
  })
}
